import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { CircleCheck as CheckCircle2, Circle as XCircle, Lightbulb, ExternalLink, Play } from 'lucide-react';

interface Suggestion {
  id: string;
  kind: 'add_question' | 'remove_question' | 'add_objection' | 'update_range' | 'add_flag' | 'reword';
  suggested_change: Record<string, unknown>;
  reasoning: string;
  evidence: Record<string, unknown>;
  status: 'pending' | 'approved' | 'rejected' | 'edited';
  created_at: string;
  based_on_script_id: string | null;
}

const KIND_LABELS: Record<Suggestion['kind'], string> = {
  add_question: 'Adicionar pergunta',
  remove_question: 'Remover pergunta',
  add_objection: 'Nova objecao',
  update_range: 'Ajustar faixa de valor',
  add_flag: 'Novo sinal',
  reword: 'Reescrever',
};

const KIND_TONES: Record<Suggestion['kind'], string> = {
  add_question: 'bg-blue-100 text-blue-700',
  remove_question: 'bg-red-100 text-red-700',
  add_objection: 'bg-amber-100 text-amber-700',
  update_range: 'bg-emerald-100 text-emerald-700',
  add_flag: 'bg-slate-200 text-slate-700',
  reword: 'bg-indigo-100 text-indigo-700',
};

export default function SuggestionsTab({ sellerId }: { sellerId: string }) {
  const { user } = useAuth();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [statusFilter, setStatusFilter] = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [lastRunMessage, setLastRunMessage] = useState<string | null>(null);

  useEffect(() => {
    void load();
  }, [sellerId, statusFilter]);

  async function generateNow() {
    if (!user) return;
    setGenerating(true);
    setLastRunMessage(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      const resp = await fetch(
        (import.meta.env.VITE_SUPABASE_URL as string) + '/functions/v1/ai-weekly-suggestions',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
            apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
          },
          body: JSON.stringify({ seller_id: sellerId, run_by: user.id }),
        }
      );
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(json.error || 'Falha ao gerar sugestoes');
      if (json.message === 'no_recent_activity') {
        setLastRunMessage('Nao houve conversas nos ultimos 7 dias para analisar.');
      } else {
        setLastRunMessage(
          `Analisadas ${json.conversations_analyzed} conversas. ${json.suggestions_generated} nova(s) sugestao(oes).`
        );
      }
      setStatusFilter('pending');
      await load();
    } catch (err) {
      setLastRunMessage('Erro: ' + (err as Error).message);
    } finally {
      setGenerating(false);
    }
  }

  async function load() {
    setLoading(true);
    let query = supabase
      .from('ai_script_suggestions')
      .select('*')
      .eq('seller_id', sellerId)
      .order('created_at', { ascending: false })
      .limit(100);
    if (statusFilter !== 'all') query = query.eq('status', statusFilter);
    const { data } = await query;
    setSuggestions((data ?? []) as Suggestion[]);
    setLoading(false);
  }

  async function decide(id: string, decision: 'approved' | 'rejected') {
    if (!user) return;
    await supabase
      .from('ai_script_suggestions')
      .update({ status: decision, reviewed_by: user.id, reviewed_at: new Date().toISOString() })
      .eq('id', id);

    if (decision === 'approved') {
      const s = suggestions.find((x) => x.id === id);
      if (s) await applyToActiveScript(s);
    }
    void load();
  }

  async function applyToActiveScript(s: Suggestion) {
    const { data: active } = await supabase
      .from('ai_agent_scripts')
      .select('*')
      .eq('seller_id', sellerId)
      .eq('is_active', true)
      .maybeSingle();
    if (!active) return;

    const cur = active as any;
    const change = s.suggested_change as any;
    const updates: any = {};

    if (s.kind === 'add_question' && change.question) {
      updates.questions = [...(cur.questions ?? []), change.question];
    } else if (s.kind === 'remove_question' && change.question) {
      updates.questions = (cur.questions ?? []).filter((q: string) => q !== change.question);
    } else if (s.kind === 'add_objection' && change.objection && change.response) {
      updates.objection_handlers = [
        ...(cur.objection_handlers ?? []),
        { objection: change.objection, response: change.response },
      ];
    } else if (s.kind === 'update_range' && change.value_ranges) {
      updates.value_ranges = { ...(cur.value_ranges ?? {}), ...change.value_ranges };
    } else if (s.kind === 'add_flag' && change.flag) {
      const target = change.flag_type === 'red' ? 'red_flags' : 'red_flags';
      updates[target] = [...(cur[target] ?? []), change.flag];
    }

    if (Object.keys(updates).length > 0) {
      await supabase.from('ai_agent_scripts').update(updates).eq('id', cur.id);
    }
  }

  if (loading) return <div className="text-sm text-slate-500">Carregando...</div>;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-amber-500" />
              <div className="text-sm font-semibold text-slate-900">Sugestoes do agente</div>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Uma vez por semana o agente cruza conversas + resultado dos contratos e propoe pequenos ajustes no script.
              Nada vai para o script sem sua aprovacao.
            </p>
          </div>
          <button
            onClick={generateNow}
            disabled={generating}
            className="inline-flex items-center gap-2 self-start rounded-lg bg-emerald-600 px-3 py-2 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
          >
            <Play className="h-3.5 w-3.5" />
            {generating ? 'Gerando...' : 'Gerar sugestoes agora'}
          </button>
        </div>
        {lastRunMessage && (
          <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            {lastRunMessage}
          </div>
        )}
      </div>

      <div className="flex gap-1 rounded-lg bg-slate-100 p-1 text-xs">
        {(['pending', 'approved', 'rejected', 'all'] as const).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={
              'flex-1 rounded-md px-2 py-1.5 font-medium transition ' +
              (statusFilter === s ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900')
            }
          >
            {s === 'pending' ? 'Pendentes' : s === 'approved' ? 'Aprovadas' : s === 'rejected' ? 'Recusadas' : 'Todas'}
          </button>
        ))}
      </div>

      {suggestions.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500">
          Nenhuma sugestao {statusFilter === 'pending' ? 'pendente' : ''} no momento.
        </div>
      )}

      <div className="space-y-3">
        {suggestions.map((s) => (
          <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <span className={'rounded-full px-2 py-0.5 text-[10px] font-medium ' + KIND_TONES[s.kind]}>
                {KIND_LABELS[s.kind]}
              </span>
              <StatusBadge status={s.status} />
              <span className="ml-auto text-xs text-slate-400">
                {new Date(s.created_at).toLocaleString('pt-BR')}
              </span>
            </div>

            <div className="rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm text-slate-800">
              <ChangePreview kind={s.kind} change={s.suggested_change} />
            </div>

            <div className="mt-3 text-sm text-slate-700">
              <span className="font-medium text-slate-600">Por que:</span> {s.reasoning}
            </div>

            {s.evidence && Object.keys(s.evidence).length > 0 && (
              <details className="mt-2 text-xs text-slate-500">
                <summary className="cursor-pointer select-none hover:text-slate-700">
                  Ver evidencias <ExternalLink className="inline h-3 w-3" />
                </summary>
                <pre className="mt-2 overflow-x-auto rounded bg-slate-100 p-2 text-[11px]">
                  {JSON.stringify(s.evidence, null, 2)}
                </pre>
              </details>
            )}

            {s.status === 'pending' && (
              <div className="mt-4 flex justify-end gap-2">
                <button
                  onClick={() => decide(s.id, 'rejected')}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <XCircle className="h-4 w-4" /> Recusar
                </button>
                <button
                  onClick={() => decide(s.id, 'approved')}
                  className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700"
                >
                  <CheckCircle2 className="h-4 w-4" /> Aprovar e aplicar
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: Suggestion['status'] }) {
  const tones = {
    pending: 'bg-amber-100 text-amber-700',
    approved: 'bg-emerald-100 text-emerald-700',
    rejected: 'bg-slate-200 text-slate-700',
    edited: 'bg-blue-100 text-blue-700',
  } as const;
  const labels = {
    pending: 'Pendente',
    approved: 'Aprovada',
    rejected: 'Recusada',
    edited: 'Editada',
  } as const;
  return <span className={'rounded-full px-2 py-0.5 text-[10px] font-medium ' + tones[status]}>{labels[status]}</span>;
}

function ChangePreview({ kind, change }: { kind: Suggestion['kind']; change: Record<string, unknown> }) {
  const c = change as any;
  if (kind === 'add_question') return <p>Adicionar pergunta: "{c.question}"</p>;
  if (kind === 'remove_question') return <p>Remover pergunta: "{c.question}"</p>;
  if (kind === 'add_objection')
    return (
      <div>
        <p className="font-medium">Se o lead disser:</p>
        <p className="mb-2">"{c.objection}"</p>
        <p className="font-medium">Responder:</p>
        <p>"{c.response}"</p>
      </div>
    );
  if (kind === 'update_range')
    return (
      <p>
        Ajustar faixas: min {c.value_ranges?.min ?? '-'}, ideal {c.value_ranges?.sweet_spot ?? '-'}, max{' '}
        {c.value_ranges?.max ?? '-'}
      </p>
    );
  if (kind === 'add_flag') return <p>Novo sinal ({c.flag_type ?? 'red'}): "{c.flag}"</p>;
  if (kind === 'reword')
    return (
      <div>
        <p className="text-xs text-slate-500">Antes:</p>
        <p className="mb-2">"{c.before}"</p>
        <p className="text-xs text-slate-500">Depois:</p>
        <p>"{c.after}"</p>
      </div>
    );
  return <pre className="text-xs">{JSON.stringify(c, null, 2)}</pre>;
}
